import {
  Button,
  Divider,
  Drawer,
  Popconfirm,
  Space,
  Table,
  TableProps,
  Tag,
  Typography,
  message,
} from 'antd'
import dayjs from 'dayjs'
import { memo, useCallback, useEffect, useRef, useState } from 'react'

import StudentAvatar from '~/components/StudentAvatar'
import { deleteStudent, getStudent } from '~/services/student'
import { isAdmin } from '~/utils/auth'

import { IStudent } from '../../types/student'
import DesiredBookingModal from './DesiredBookingModal'
import ModalForm from './ModalForm'
import OccupiedSlotsDrawer from './OccupiedSlotsDrawer'
import TextbookProgressModal from './TextbookProgressModal'
import { Container } from './style'

const Index = () => {
  const [loading, setLoading] = useState(false)
  const [dataSource, setDataSource] = useState<IStudent[]>([])
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [drawerFormData, setDrawerFormData] = useState<Partial<IStudent>>({})
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [size, setSize] = useState(10)
  const [bookingStudent, setBookingStudent] = useState<IStudent | null>(null)
  const [textbookStudent, setTextbookStudent] = useState<IStudent | null>(null)
  const [slotsStudent, setSlotsStudent] = useState<IStudent | null>(null)

  const onPageChange = useCallback((cPage: number, cSize: number) => {
    setPage(cPage)
    setSize(cSize)
  }, [])
  const [admin] = useState(isAdmin())

  const fetchData = useCallback(() => {
    setLoading(true)
    getStudent(page, size)
      .then((res) => {
        setDataSource(res.data?.content || [])
        setTotal(res.data?.total || 0)
      })
      .catch((err) => {
        message.error(err?.message || '请求错误，请稍后再试')
      })
      .finally(() => {
        setLoading(false)
      })
  }, [page, size])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const onSearch = useCallback(() => {
    fetchData()
  }, [fetchData])

  const columns = useRef<TableProps<IStudent>['columns']>([
    {
      title: '学生姓名',
      dataIndex: 'name',
      key: 'name',
      width: 150,
      fixed: 'left',
    },
    {
      title: '学生状态',
      key: 'studentStatus',
      width: 220,
      render: (_, record) => {
        const status = record.studentStatus?.status
        if (!status) return '—'
        const labels = {
          NEW_USER: '新用户',
          TRIAL_AWAITING_BOOKING: '试听用户·待预约',
          TRIAL_BOOKED: '试听用户·已预约待上课',
          AWAITING_CONVERSION: '待转化用户',
          CONVERTED: '成功购课用户',
        }
        return <Tag>{labels[status] || '—'}</Tag>
      },
    },
    {
      title: '头像',
      dataIndex: 'profileImgUrl',
      key: 'profileImgUrl',
      width: 90,
      render: (_, record) => (
        <StudentAvatar
          profileImgUrl={record.profileImgUrl}
          name={record.name}
        />
      ),
    },
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 100,
    },
    {
      title: '用户ID',
      dataIndex: 'userId',
      key: 'userId',
      width: 100,
    },
    ...(admin
      ? [
          {
            title: '占用时段',
            key: 'occupiedSlots',
            width: 130,
            render: (_: unknown, record: IStudent) => (
              <Typography.Link onClick={() => setSlotsStudent(record)}>
                查看占用时段
              </Typography.Link>
            ),
          },
        ]
      : []),
    {
      title: '代理人',
      dataIndex: 'agent',
      key: 'agent',
      width: 150,
      render: (text, record) => {
        if (!record.agent) return '-'
        return (
          <div>
            <div>ID: {record.agent.id}</div>
            <div>姓名：{record.agent.name}</div>
            <div>电话：{record.agent.phone}</div>
          </div>
        )
      },
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
      width: 200,
    },
    {
      title: '电话',
      dataIndex: 'phone',
      key: 'phone',
      width: 150,
    },
    {
      title: '年龄',
      dataIndex: 'age',
      key: 'age',
      width: 150,
    },

    {
      title: '更新时间',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (text) => dayjs(text).format('YYYY-MM-DD HH:mm:ss'),
      width: 180,
    },
    {
      title: '注册时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text) => dayjs(text).format('YYYY-MM-DD HH:mm:ss'),
      width: 180,
    },
    {
      title: '操作',
      dataIndex: 'action',
      key: 'action',
      render: (text, record) => {
        return (
          <Space>
            {admin && (
              <Typography.Link onClick={() => setBookingStudent(record)}>
                代约期望时间
              </Typography.Link>
            )}
            {admin && (
              <Typography.Link onClick={() => setTextbookStudent(record)}>
                教材管理
              </Typography.Link>
            )}
            <Typography.Link
              onClick={() => {
                setDrawerFormData(record)
                setIsDrawerOpen(true)
              }}
              disabled={!admin}
            >
              编辑
            </Typography.Link>
            <Divider style={{ marginInline: 0 }} type="vertical" />
            <Popconfirm
              title="提示"
              description="确认删除？"
              onConfirm={() => {
                deleteStudent(record.id)
                  .then(() => {
                    message.success('删除成功')
                    fetchData()
                  })
                  .catch((err) => {
                    message.error(err?.message || '删除失败，请稍后再试～')
                  })
              }}
              okText="删除"
              cancelText="取消"
            >
              <Typography.Link type="danger" disabled={!admin}>
                删除
              </Typography.Link>
            </Popconfirm>
          </Space>
        )
      },
      fixed: 'right',
      width: 280,
      align: 'center',
    },
  ])

  const addData = useCallback(() => {
    setIsDrawerOpen(true)
  }, [])
  const closeDrawer = useCallback(() => {
    setDrawerFormData({})
    setIsDrawerOpen(false)
  }, [])

  return (
    <Container>
      <div className="top">
        <Button
          style={{ marginRight: 12 }}
          type="primary"
          loading={loading}
          onClick={onSearch}
        >
          查询
        </Button>
        <Button onClick={addData} disabled={!admin}>
          新增
        </Button>
      </div>
      <div className="table">
        <Table
          rowKey="id"
          scroll={{ x: 2000, y: 500 }}
          loading={loading}
          columns={columns.current}
          dataSource={dataSource}
          pagination={{
            total,
            pageSize: size,
            current: page,
            onChange: onPageChange,
          }}
        />
      </div>
      <div className="pagination"></div>
      {slotsStudent && (
        <OccupiedSlotsDrawer
          key={slotsStudent.id}
          student={slotsStudent}
          onClose={() => setSlotsStudent(null)}
        />
      )}
      {bookingStudent && (
        <DesiredBookingModal
          student={bookingStudent}
          onClose={() => setBookingStudent(null)}
        />
      )}
      {textbookStudent && (
        <TextbookProgressModal
          student={textbookStudent}
          onClose={() => setTextbookStudent(null)}
        />
      )}
      <Drawer
        title={drawerFormData.userId ? '编辑学生' : '新增学生'}
        open={isDrawerOpen}
        onClose={closeDrawer}
        width={450}
        destroyOnClose
      >
        <ModalForm data={drawerFormData} refreshTable={fetchData} />
      </Drawer>
    </Container>
  )
}

export default memo(Index)
